import { subscriptionsItem } from '@gate31/touch/components/edit-subscriptions/edit-subscription-page-form/edit-subscription-page-form';
import { WarehouseResponse } from '@gate31/types/warehouse';
import { OrderStatus } from '@gate31/types/order-statuses';
import { stringify } from '../libs/query-string';

export const HOST_GATE31_API = 'https://api.aerogate31.com/gates/';
export const HOST_GATE31_API_V2 = 'https://api.aerogate31.com/v2';

class Gate31ApiError extends Error { name = 'ApiGate31Error' }

interface params {
    [key: string]: string;
}
interface body {
    // eslint-disable-next-line
    [key: string]: body | any;
}
interface headers {
    'Content-Type'?: string;
    Authorization?: string;
}

export interface UpdateAccountBodyInt {
    userId: number;
    userName?: string;
    userSurname?: string;
    userPatronymic?: string;
    userPhone: string;
    userEmail: string;
    userDateOfBirthday?: string;
    passwordInfo?: {
        isUpdate: boolean;
        newPassword: string;
        newPasswordConfirmation: string;
    };
    userFavorites?: string;
}
export interface UpdateAccountResponseErrorInt {
    error: {
       code: string;
       message: string;
    };
}
export interface UpdateAccountResponseInt {
    response: 'ok';
}

interface CreateAccountBody {
    email: string;
    phone: string;
    dateOfBirth: string | null;
    name: {
        firstName : string;
        lastName: string | null;
    };
    password: string | null;
    endpoint: {
        shop: string;
    };
    subscriptions: {
        email: boolean;
        phone: boolean;
    };
}

export interface HistoryResponseInt {
    level: {
        nextLevelBalance: number;
        nextLevelName: string;
    };
    history: {
        changes: {earned: number; spent: number};
        date: string;
        id: number;
        name: string;
    }[];
}

type GetLoyaltyUserResponseType = {
    customer: any;
    retailOrderStatistics: any;
    status: any;
    balances: {
        available: number;
        blocked: number;
        systemName: string;
    }[];
    customerSegmentations: {
        segmentation: { name: string };
        segment: { name: string };
    }[];
}

export class Gate31ApiV2 {
    static headers = {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json'
    }

    static mindboxGetUserSubscribeList(body: {email: string; deviceUUID: string}) {
        return Gate31ApiV2.post<any>(body, '/mindbox/website/SubscribeInfo');
    }

    static mindboxGetUserSubscribeListByTicket(body: {deviceUUID: string; ticket: string}) {
        return Gate31ApiV2.post<any>(body, '/mindbox/website/getClientByTicket');
    }

    static mindboxSignup(body: {
        customer: {
            email: string;
            fullName: string;
            mobilePhone: string;
            subscriptions: {
                isSubscribed: string;
            }[];
        };
        deviceUUID: string;
    }) {
        return Gate31ApiV2.post<any>(body, '/mindbox/website/signup');
    }

    static mindboxEditProfile(body: {
        customer: {
            email: string;
            fullName: string;
            mobilePhone: string;
            ids: {
                siteid: any;
            };
            subscriptions: {
                brand: string;
                pointOfContact: string;
                isSubscribed: string;
            }[];
        };
        deviceUUID: any;
    }) {
        return Gate31ApiV2.post<any>(body, '/mindbox/website/edit');
    }

    static mindboxUpdateSubsByEmail(body: {
        email: string;
        subscriptions: Array<subscriptionsItem>;
        deviceUUID: string;
    }) {
        return Gate31ApiV2.post<any>(body, '/mindbox/website/Redak');
    }

    static mindboxUpdateSubsByTicket(body: {
        authenticationTicket: string;
        subscriptions: Array<subscriptionsItem>;
        deviceUUID: string;
    }) {
        return Gate31ApiV2.post<any>(body, '/mindbox/website/RedakByTiket');
    }

    static promoCodeActivation(body: { code: string; phone: string; name: string; lastName: string; email: string }) {
        return Gate31ApiV2.post<any>(body, '/maxma/promo-code-activation');
    }

    static configPhoneSendCode({ phone, code }: { phone: string; code: string }) {
        return Gate31ApiV2.post<any>({ phoneNumber: phone, code }, '/user/confirm-phone/send-code');
    }

    static configPhoneGetCode(phone: string, token: string) {
        return Gate31ApiV2.post<any>({ phoneNumber: phone, token }, '/user/confirm-phone/get-code-test');
    }

    static createAccount(body: CreateAccountBody) {
        return Gate31ApiV2.post<any>(body, '/user/create-user-process');
    }

    static calculatePurchase(body: any) {
        return Gate31ApiV2.post<any>(body, '/programm-loyalty/calculate-purchase');
    }

    static getBalanceHistory(phone: string) {
        return Gate31ApiV2.get<HistoryResponseInt>({ phoneNamuber: phone }, '/programm-loyalty/get-banace-history', {});
    }

    static getWarehouseStock(id: string) {
        return Gate31ApiV2.get<WarehouseResponse>({ id }, '/warehouse/get-stock', null);
    }

    static updateAccount(body: UpdateAccountBodyInt) {
        type UpdateProfileResponseType = UpdateAccountResponseInt | UpdateAccountResponseErrorInt
        return Gate31ApiV2.post<UpdateProfileResponseType>(body, '/user/update-client-profile');
    }

    static loyaltyGetUser(phone: string) {
        return Gate31ApiV2.get<GetLoyaltyUserResponseType>({ phone }, '/programm-loyalty/get-balance', {});
    }

    static getCustomStatus() {
        return Gate31ApiV2.get<Array<OrderStatus>>({}, '/insales/custom-statuses', null).then(response => response || []);
    }

    private static post<T>(body: body, path: string): Promise<T> {
        const url = `${HOST_GATE31_API_V2}${path}`;

        return fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: { ...Gate31ApiV2.headers }
        })
            .then(res => res.json() as T)
            .then(response => response)
            .catch(error => {
                throw new Gate31ApiError(`Ошибка в обращении к ApiGate - ${url} - ${error}`);
            });
    }

    private static get<T>(params: params | null, path: string, headers: headers | null): Promise<T> {
        const url = `${HOST_GATE31_API_V2}${path}?${stringify({ ...params }, null)}`;

        return fetch(url, {
            method: 'GET',
            headers: { ...headers, ...Gate31ApiV2.headers }
        })
            .then(response => response.json())
            .then((response: T) => {
                return response;
            })
            .catch(err => {
                throw new Gate31ApiError(`Ошибка в обращении к ApiGate - ${url} - ${err}`);
            });
    }
}
